import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Pencil, FileDown, Mail, Camera, Paperclip, Trash2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl, orderPdfUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  StatusBadge, PriorityBadge, ElapsedTime,
  SERVICE_LABELS, MODALITY_LABELS,
} from "../components/badges";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

function InfoItem({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900" data-testid={testId}>{value || "-"}</p>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [uploading, setUploading] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const photoInput = useRef(null);
  const docInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar la orden"));
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (status) => {
    try {
      const payload = {
        client_id: order.client_id,
        technician_id: order.technician_id,
        service_type: order.service_type,
        priority: order.priority,
        date: order.date,
        start_time: order.start_time,
        end_time: order.end_time,
        modality: order.modality,
        equipment: order.equipment,
        problem_description: order.problem_description,
        diagnosis: order.diagnosis,
        materials: order.materials || [],
        status,
        observations: order.observations,
      };
      await api.put(`/orders/${id}`, payload);
      toast.success(`Estado actualizado a ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "No se pudo actualizar el estado"));
    }
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${id}/files?kind=${kind}`, fd);
      toast.success(kind === "photo" ? "Foto adjuntada" : "Documento adjuntado");
      load();
    } catch (e) {
      toast.error(apiError(e, "No se pudo subir el archivo"));
    } finally {
      setUploading("");
    }
  };

  const removeFile = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      toast.success("Archivo eliminado");
      load();
    } catch (e) {
      toast.error(apiError(e, "No se pudo eliminar el archivo"));
    }
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await api.post(`/orders/${id}/send-email`);
      toast.success(`Correo enviado a ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e, "No se pudo enviar el correo"));
    } finally {
      setSendingEmail(false);
    }
  };

  const deleteOrder = async () => {
    setDeleting(true);
    try {
      await api.delete(`/orders/${id}`);
      toast.success(`Orden ${order.order_number} eliminada`);
      navigate("/");
    } catch (e) {
      toast.error(apiError(e, "No se pudo eliminar la orden"));
      setDeleting(false);
    }
  };

  if (!order) {
    return (
      <div className="space-y-3" data-testid="order-detail-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const photos = (order.files || []).filter((f) => f.kind === "photo");
  const documents = (order.files || []).filter((f) => f.kind === "document");
  const materialsTotal = (order.materials || []).reduce(
    (s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0
  );

  return (
    <div className="mx-auto max-w-5xl space-y-5" data-testid="order-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="order-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="font-heading text-2xl font-black tracking-tight text-slate-900" data-testid="order-number">
              {order.order_number}
            </h2>
            <p className="text-xs text-slate-500">Creada el {(order.created_at || "").slice(0, 10)}</p>
          </div>
          <StatusBadge status={order.status} testId="order-status-badge" />
          {order.status !== "completado" && (
            <ElapsedTime since={order.created_at} high={order.priority === "alta"} testId="order-elapsed" />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={order.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-[150px]" data-testid="quick-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="en_progreso">En progreso</SelectItem>
              <SelectItem value="completado">Completado</SelectItem>
              <SelectItem value="escalado">Escalado</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-1.5" onClick={() => navigate(`/orders/${id}/edit`)} data-testid="edit-order-button">
            <Pencil className="h-4 w-4" /> Editar
          </Button>
          <Button variant="outline" className="gap-1.5" onClick={() => window.open(orderPdfUrl(id), "_blank")} data-testid="export-pdf-button">
            <FileDown className="h-4 w-4" /> PDF
          </Button>
          <Button
            className="gap-1.5 bg-primary hover:bg-[#0353E9]"
            onClick={sendEmail}
            disabled={sendingEmail}
            data-testid="send-email-button"
          >
            {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Enviar al cliente
          </Button>
          {user?.role === "admin" && (
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="delete-order-button"
                >
                  <Trash2 className="h-4 w-4" /> Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading">¿Eliminar orden {order.order_number}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer. Se eliminará la orden de servicio y sus archivos adjuntos.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-order-cancel">Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteOrder}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="delete-order-confirm"
                  >
                    {deleting ? "Eliminando..." : "Eliminar orden"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4 lg:col-span-2">
          <h3 className="font-heading text-base font-bold text-slate-900">Detalle del Servicio</h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <InfoItem label="Cliente" value={
              <Link to={`/clients/${order.client_id}`} className="text-primary hover:underline" data-testid="order-client-link">
                {order.client_name}
              </Link>
            } />
            <InfoItem label="Técnico" value={order.technician_name} testId="order-technician" />
            <InfoItem label="Fecha" value={(order.date || "").slice(0, 10)} />
            <InfoItem label="Tipo de servicio" value={SERVICE_LABELS[order.service_type]} />
            <InfoItem label="Prioridad" value={<PriorityBadge priority={order.priority} />} />
            <InfoItem label="Modalidad" value={MODALITY_LABELS[order.modality]} />
            <InfoItem label="Hora inicio" value={order.start_time ? String(order.start_time).replace("T", " ").slice(0, 16) : "-"} />
            <InfoItem label="Hora fin" value={order.end_time ? String(order.end_time).replace("T", " ").slice(0, 16) : "-"} />
            <InfoItem label="Equipo / Sistema" value={order.equipment} testId="order-equipment" />
          </div>
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Descripción del problema</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800" data-testid="order-problem">{order.problem_description || "-"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Diagnóstico y trabajo realizado</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800" data-testid="order-diagnosis">{order.diagnosis || "-"}</p>
            </div>
            {order.observations && (
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Observaciones</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800" data-testid="order-observations">{order.observations}</p>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3">
          <h3 className="font-heading text-base font-bold text-slate-900">Cliente</h3>
          {order.client ? (
            <div className="space-y-3">
              <InfoItem label="Nombre / Empresa" value={order.client.name} />
              <InfoItem label="RUC / Cédula" value={order.client.tax_id} />
              <InfoItem label="Contacto" value={order.client.contact_person} />
              <InfoItem label="Teléfono" value={order.client.phone} />
              <InfoItem label="Correo" value={order.client.email} />
              <InfoItem label="Ubicación" value={order.client.location} />
              <InfoItem label="Dirección" value={order.client.address} />
            </div>
          ) : (
            <p className="text-sm text-slate-400">Sin datos del cliente.</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="font-heading text-base font-bold text-slate-900">Materiales Utilizados</h3>
        {(order.materials || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-400">Sin materiales registrados.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-4">Material</th>
                  <th className="py-2 pr-4">Cantidad</th>
                  <th className="py-2 pr-4">Costo Unit.</th>
                  <th className="py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {order.materials.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`material-item-${i}`}>
                    <td className="py-2 pr-4">{m.name}</td>
                    <td className="py-2 pr-4 font-mono">{m.quantity}</td>
                    <td className="py-2 pr-4 font-mono">${(m.unit_cost || 0).toFixed(2)}</td>
                    <td className="py-2 text-right font-mono">${((m.quantity || 0) * (m.unit_cost || 0)).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="py-2">Total</td>
                  <td className="py-2 text-right font-mono" data-testid="materials-total-detail">${materialsTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-slate-900">Fotos</h3>
            <Button
              variant="outline" size="sm" className="gap-1.5"
              onClick={() => photoInput.current?.click()}
              disabled={uploading === "photo"}
              data-testid="upload-photo-button"
            >
              {uploading === "photo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              Adjuntar foto
            </Button>
            <input
              ref={photoInput} type="file" accept="image/*" className="hidden"
              data-testid="photo-file-input"
              onChange={(e) => { upload(e.target.files[0], "photo"); e.target.value = ""; }}
            />
          </div>
          {photos.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Sin fotos adjuntas.</p>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {photos.map((f) => (
                <div key={f.id} className="group relative overflow-hidden rounded-md border border-slate-200" data-testid={`photo-${f.id}`}>
                  <a href={fileUrl(f.id)} target="_blank" rel="noreferrer">
                    <img src={fileUrl(f.id)} alt={f.original_filename} className="h-28 w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                  </a>
                  <button
                    onClick={() => removeFile(f.id)}
                    data-testid={`photo-delete-${f.id}`}
                    className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-slate-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-slate-900">Documentos</h3>
            <Button
              variant="outline" size="sm" className="gap-1.5"
              onClick={() => docInput.current?.click()}
              disabled={uploading === "document"}
              data-testid="upload-document-button"
            >
              {uploading === "document" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              Adjuntar documento
            </Button>
            <input
              ref={docInput} type="file" className="hidden"
              accept=".pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,image/*"
              data-testid="document-file-input"
              onChange={(e) => { upload(e.target.files[0], "document"); e.target.value = ""; }}
            />
          </div>
          {documents.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">Sin documentos adjuntos.</p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100">
              {documents.map((f) => (
                <li key={f.id} className="flex items-center justify-between py-2" data-testid={`document-${f.id}`}>
                  <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                    <Paperclip className="h-4 w-4" />
                    <span className="truncate">{f.original_filename}</span>
                  </a>
                  <button
                    onClick={() => removeFile(f.id)}
                    data-testid={`document-delete-${f.id}`}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
